import getWeights from "../../utils/getWeights";
import {Box, Chip, CircularProgress, IconButton, Popover, Stack, Tooltip, Typography} from "@mui/material";
import React, {useEffect, useState, useSyncExternalStore} from "react";
import axios from "axios";
import {GridEditInputCell, useGridApiContext} from "@mui/x-data-grid-premium";
import {tooltipClasses} from '@mui/material/Tooltip';
import {styled} from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import {DateTime} from "luxon";
import {TextSnippetOutlined, WarningAmberOutlined} from "@mui/icons-material";
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ExpressIconSrc from './express.svg';
import ExpressManualIconSrc from './express_manual.svg';
import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from '@mui/icons-material/Warning';
import {getGridStringOperators} from '@mui/x-data-grid-premium';
import {
    dateValueFormatter,
    dateValueGetter,
    timestampValueFormatter,
    timestampValueFormatterUtc
} from "@lb/mlib-components";


let salesorderid = {
    field: 'salesorderid',
    type: 'string',
    disablePadding: false,
    headerName: 'Sales Order #',
    width: 160,
    renderCell: (params) => (<Stack direction={"row"} spacing={1} sx={{
        alignItems: 'center'
    }}>
        <Typography variant={"body2"}>
            {params.row.salesorderid}
        </Typography>
        {['fedex_manual', 'fedex_auto'].includes(params.row.courier_brand) &&
            [2].includes(params.row.fedexstatus) && <Chip label="FedEx Hold" size={'small'}/>}
        {params.row.hold_prod && <Chip label={'On Hold'} size={'small'}/>}
    </Stack>)
}

export const BundleStatus = {
    NOT_OPTIMIZED: 'Not_Optimized',
    NOT_EVALUATED: 'Not_Evaluated',
    APPROVED: 'Approved',
};

export const BUNDLE_STATUS_CONFIG = {
    [BundleStatus.NOT_OPTIMIZED]: {
        label: 'Not Optimized',
        color: 'default',
    },
    [BundleStatus.NOT_EVALUATED]: {
        label: 'Not Evaluated',
        color: 'info',
    },
    [BundleStatus.APPROVED]: {
        label: 'Approved',
        color: 'success',
    },
};

export const BundleWarnings = {
    COUNT_EXCEEDED: 'Bundle_Count_Exceeded',
    WEIGHT_EXCEEDED: 'Bundle_Weight_Exceeded',
    COUNT_WEIGHT_EXCEEDED: 'Bundle_Count_Weight_Exceeded',

    COUNT_UNDER: 'Bundle_Count_Under',
    WEIGHT_UNDER: 'Bundle_Weight_Under',
    COUNT_WEIGHT_UNDER: 'Bundle_Count_and_Weight_Under',
};

export const BUNDLE_WARNING_CONFIG = {
    [BundleWarnings.COUNT_EXCEEDED]: {
        label: 'Bundle count exceeded',
    },

    [BundleWarnings.WEIGHT_EXCEEDED]: {
        label: 'Bundle weight exceeded',
    },

    [BundleWarnings.COUNT_WEIGHT_EXCEEDED]: {
        label: 'Bundle count and weight exceeded',
    },

    [BundleWarnings.COUNT_UNDER]: {
        label: 'Bundle count under approved value',
    },

    [BundleWarnings.WEIGHT_UNDER]: {
        label: 'Bundle weight under approved value',
    },

    [BundleWarnings.COUNT_WEIGHT_UNDER]: {
        label: 'Bundle count and weight under approved values',
    },
};

function BundleWarningsCell({row}) {
    const warning =
        row?.bundle_status?.bundle_warning;

    if (!warning) {
        return null;
    }

    const config =
        BUNDLE_WARNING_CONFIG[warning];

    if (!config) {
        return null;
    }

    return (
        <Tooltip title={config.label}>
            <WarningIcon
                fontSize="small"
                color="warning"
            />
        </Tooltip>
    );
}

function BundleWarningsIcon({value}) {
    return value ? (
        <WarningIcon
            fontSize="small"
            color="warning"
        />
    ) : null;
}

export function BundleStatusChip({value, size = "small"}) {

    if (!value) {
        value = BundleStatus.NOT_OPTIMIZED
    }
    const config = BUNDLE_STATUS_CONFIG[value];

    if (!config) return null;

    return (
        <Chip
            label={config.label}
            color={config.color}
            size={size}
        />
    );
}

let bundle_status = {
    field: 'bundle_opt_status',
    type: 'singleSelect',
    headerName: 'Bundle Opt. Status',
    width: 200,
    valueOptions: Object.values(BundleStatus).map((value) => ({
        value,
        label: BUNDLE_STATUS_CONFIG[value]?.label || value,
    })),
    renderCell: (params) => (
        <BundleStatusChip value={params.value}/>
    ),
};


let bundle_warning = {
    field: 'has_warning',
    headerName: 'Bundle Warning',
    width: 120,
    type: 'boolean',
    renderCell: (params) => (
        <BundleWarningsIcon value={params.value}/>
    ),
};

function ShippingNotesCell({note}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);
    const id = open ? "shipping-notes-popover" : undefined;

    const handleToggle = (e) => {
        e.stopPropagation();
        setAnchorEl(open ? null : e.currentTarget);
    };

    const handleClose = (e) => {
        e.stopPropagation();
        setAnchorEl(null);
    };

    return (
        <Box sx={{display: "flex", alignItems: "center"}}>
            <Tooltip title="View shipping note" placement="top" arrow>
                <IconButton
                    size="small"
                    aria-describedby={id}
                    onClick={handleToggle}
                    sx={{
                        p: 0.25,
                        opacity: 0.9,
                        transition: "transform 0.15s ease-in-out",
                        "&:hover": {transform: "scale(1.2)"},
                    }}
                >
                    <TextSnippetOutlined color="info" fontSize="small"/>
                </IconButton>
            </Tooltip>
            <Popover
                id={id}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                slotProps={{paper: {sx: {maxWidth: 420, width: "max-content"}}}}
            >
                <Box sx={{p: 1.5}}>
                    <Stack
                        direction="row"
                        sx={{
                            alignItems: "center",
                            justifyContent: "space-between",
                            mb: 1
                        }}>
                        <Typography variant="subtitle2">Shipping Notes</Typography>
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small"/>
                        </IconButton>
                    </Stack>
                    <Typography variant="body2" sx={{whiteSpace: "pre-wrap"}}>
                        {note}
                    </Typography>
                </Box>
            </Popover>
        </Box>
    );
}

export function renderShipmentIdWithFlag(params) {
    const note = params?.row?.shippingnotes;
    if (!note) return <Box/>;

    return (
        <Box sx={{display: "flex", alignItems: "center"}}>
            <ShippingNotesCell note={note}/>
        </Box>
    );
}

let shipping_notes = {
    field: "shippingnotes",
    type: "string",
    disablePadding: false,
    headerName: "Shipping Notes",
    width: 135,
    renderCell: renderShipmentIdWithFlag,
    filterOperators: getGridStringOperators().filter(op =>
        ['isEmpty', 'isNotEmpty'].includes(op.value)
    ),
};

let shipmentid = {
    field: 'shipmentid',
    type: 'string',
    disablePadding: false,
    headerName: 'Shipment #',
    width: 120,
}

let finishes = {
    field: 'finishes',
    type: 'string',
    disablePadding: false,
    headerName: 'Finishes',
    width: 300,
    renderCell: ({row}) => {
        return Array.isArray(row.finishes) ? <Stack direction={"row"} spacing={1}>{(row.finishes?.map((item) => (
            <Chip label={item}/>)))}</Stack> : <>{row.finishes}</>
    }
}
export const StyledTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} classes={{popper: className}}/>))(({theme}) => ({
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.error.main, color: theme.palette.error.contrastText,
    },
}));

// processRowUpdate's own async save has no built-in loading state (isProcessingProps only
// covers preProcessEditCellProps validation), so this tracks it externally: a consumer marks
// a cell saving around its own save call, and the edit cell borrows GridEditInputCell's
// existing spinner for it.
const savingCells = new Set();
const savingListeners = new Set();

export function setCellSaving(id, field, saving) {
    const key = `${id}:${field}`;
    if (saving) savingCells.add(key); else savingCells.delete(key);
    savingListeners.forEach((listener) => listener());
}

function useCellSaving(id, field) {
    const key = `${id}:${field}`;
    return useSyncExternalStore(
        (listener) => {
            savingListeners.add(listener);
            return () => savingListeners.delete(listener);
        },
        () => savingCells.has(key)
    );
}

export function NameEditInputCell(props) {
    const {error, id, field, isProcessingProps} = props;
    const saving = useCellSaving(id, field);

    // MUI's own isProcessingProps indicator is a static icon, not an animated spinner - swap
    // in a real one so it actually reads as "saving" rather than just a different icon. The
    // adornment docks flush to the cell's far edge by default; pull it back toward the value.
    const endAdornment = (isProcessingProps || saving)
        ? <CircularProgress size={16} color="inherit" sx={{mr: 1.5}}/>
        : undefined;

    return (<StyledTooltip open={!!error} title={error}>
        <GridEditInputCell {...props} endAdornment={endAdornment}/>
    </StyledTooltip>);
}

export function renderEdit(params) {
    return <NameEditInputCell {...params} />;
}

export function validatePriority(value) {
    if (value < 0) return 'Minimum value of 0'; else return null
}

function PriorityCell(params) {
    const apiRef = useGridApiContext();

    const startEdit = (e) => {
        e.stopPropagation();
        apiRef.current.startCellEditMode({id: params.id, field: params.field});
    };

    return (
        <Stack direction={"row-reverse"} onClick={startEdit} sx={{
            alignItems: "center",
            width: "100%",
            height: "100%",
            cursor: "pointer"
        }}>
            <IconButton sx={{color: '#afaeae'}} size={"small"} tabIndex={-1}>
                <EditIcon fontSize='inherit'/>
            </IconButton>
            <Typography variant={"body2"}>{params.row.priority}</Typography>
        </Stack>
    );
}

let priority = {
    field: 'priority',
    type: 'number',
    disablePadding: false,
    headerName: 'Priority',
    width: 100,
    editable: true,
    preProcessEditCellProps: (params) => {
        const errorMessage = validatePriority(+params.props.value);
        return {...params.props, error: errorMessage};
    },
    renderEditCell: renderEdit,
    renderCell: (params) => <PriorityCell {...params}/>,
}

let destination = {field: 'destination', numeric: false, disablePadding: false, headerName: 'Destination', width: 120}
let customer = {field: 'accountname', numeric: false, disablePadding: false, headerName: 'Customer', width: 120}

// seer_express_service is config, not code - fetched once from the backend (the canonical
// source) and cached here rather than kept as a hand-maintained copy that goes stale the
// moment the table changes. Module-level so every grid on the page shares one request.
let serviceLevelTitles = {};
let serviceLevelsPromise = null;

function loadServiceLevels() {
    if (!serviceLevelsPromise) {
        serviceLevelsPromise = axios.get('/courier/service-levels')
            .then(({data}) => {
                serviceLevelTitles = Object.fromEntries(data.map(s => [s.code, s.title]));
                return serviceLevelTitles;
            })
            .catch(() => serviceLevelTitles);
    }
    return serviceLevelsPromise;
}

function ServiceLevelCell({value}) {
    const [titles, setTitles] = useState(serviceLevelTitles);

    useEffect(() => {
        let active = true;
        loadServiceLevels().then((loaded) => {
            if (active) setTitles(loaded);
        });
        return () => {
            active = false;
        };
    }, []);

    return <Typography variant={"body2"}>{titles[value] || value || ""}</Typography>;
}

// Always the carrier's own mark. Manual is not a carrier - it is shown on the express
// icon, so the composite _manual assets are no longer used anywhere.
export const COURIER_BRAND_LOGO = {
    fedex_manual: 'https://content.longboardproducts.com/logo/fedex.png',
    fedex_auto: 'https://content.longboardproducts.com/logo/fedex.png',
    dhl_manual: 'https://content.longboardproducts.com/logo/dhl.png',
};

const COURIER_BRAND_LABEL = {
    fedex_manual: 'FedEx Manual',
    fedex_auto: 'FedEx Auto',
    dhl_manual: 'DHL Manual',
};

let courier = {
    field: 'courier_name', numeric: false, disablePadding: false, headerName: 'Courier', width: 120,
    renderCell: (params) => {
        const logo = COURIER_BRAND_LOGO[params.row.courier_brand];
        if (!logo) return <Typography variant={"body2"}>{params.value}</Typography>;
        return (
            <Tooltip title={COURIER_BRAND_LABEL[params.row.courier_brand]} arrow>
                <img src={logo} alt={COURIER_BRAND_LABEL[params.row.courier_brand]}
                     style={{height: 18, width: 'auto', maxWidth: 74,
                             objectFit: 'contain', display: 'block'}}/>
            </Tooltip>
        );
    }
}

let bundles = {
    field: 'bundles', numeric: true, disablePadding: false, headerName: 'Bundles', width: 120,
    type: 'number'
}
let po = {
    field: 'po', numeric: true, disablePadding: false, headerName: 'PO', width: 120,
    type: 'number'
}

let weightkg = {
    field: 'weightkg', type: 'number', disablePadding: false, headerName: 'Weight', width: 120, renderCell: getWeights
}

let epoc_created = {
    field: 'epoc_created',
    disablePadding: false, headerName: 'Created', width: 120,
    valueFormatter: (value) => value ? dateValueFormatter(dateValueGetter(value)) : 'never',
    valueGetter: dateValueGetter,
    type: 'date',
}


/*let target_arrival = {
    field: 'target_arrival',
    disablePadding: false,
    headerName: 'Target Arrival',
    width: 120,
    valueFormatter: getDateFromStringDateYear,
}*/

let epoc_requested = {
    field: 'epoc_requested',
    disablePadding: false, headerName: 'Quote Request', width: 120,
    valueGetter: dateValueGetter,
    valueFormatter: (value) => timestampValueFormatter(value),
    type: 'date',
}

let epoc_pickupest = {
    field: 'epoc_pickupest',
    disablePadding: false, headerName: 'Pickup (est)', width: 135,
    valueGetter: dateValueGetter,
    valueFormatter: (value) => timestampValueFormatter(value),
    type: 'date',
}

let epoc_pickedup = {
    field: 'epoc_pickedup',
    disablePadding: false, headerName: 'Picked Up', width: 135,
    valueGetter: dateValueGetter,
    valueFormatter: (value) => timestampValueFormatter(value),
    type: 'date',
}

let epoc_packed = {
    field: 'epoc_packed',
    numeric: false, headerName: 'Packed', width: 135,
    valueGetter: dateValueGetter,
    valueFormatter: (value) => timestampValueFormatter(value),
    type: 'date',
}

let epoc_deliveryest = {
    field: 'epoc_deliveryest',
    numeric: false, headerName: 'Delivery (est)', width: 135,
    valueGetter: dateValueGetter,
    // Date-only estimate stored as a UTC-midnight epoch: render in UTC so the grid matches the
    // General tab's Delivery Date input (local time would show the previous day in US timezones).
    valueFormatter: (value) => timestampValueFormatterUtc(value),
    type: 'date',
}

export function DateWarning(params) {

    const value =
        params?.value;

    if (!(value instanceof Date))
        return '';

    const packBy =
        DateTime
            .fromJSDate(value)
            .startOf('day');

    if (!packBy.isValid)
        return '';

    const today =
        DateTime.now().startOf('day');

    const weeksGrace = 1;

    const label =
        dateValueFormatter(value);

    const showIcon =
        today > packBy.plus({weeks: weeksGrace});

    return (
        <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4
        }}>
            <span>{label}</span>

            {showIcon && (
                <Tooltip
                    title={`More than ${weeksGrace} week${
                        weeksGrace > 1 ? 's' : ''
                    } overdue`}
                >
                    <WarningAmberOutlined
                        fontSize="small"
                        sx={(theme) => ({
                            color: theme.palette.warning.main,
                            cursor: 'default',
                        })}
                    />
                </Tooltip>
            )}
        </span>
    );
}

let pack_by_base = {
    field: 'pack_by', numeric: false, headerName: 'Pack By', width: 135,
    type: 'date',
    valueGetter: dateValueGetter
}

let pack_by = {
    ...pack_by_base, valueFormatter: (value) => dateValueFormatter(value),
}

let pack_by_with_warnings = {
    ...pack_by_base, renderCell: DateWarning,
}

let plan_ship_base = {
    field: 'plan_ship', numeric: false, headerName: 'Plan Ship', width: 135,
    type: 'date',
    valueGetter: dateValueGetter
}

let plan_ship = {
    ...plan_ship_base, valueFormatter: (value) => dateValueFormatter(value),
}

let plan_ship_with_warnings = {
    ...plan_ship_base, renderCell: DateWarning,
}

let prod_release = {
    field: 'prod_release',
    numeric: false,
    headerName: 'Prod Release',
    width: 135,
    type: 'date',
    valueGetter: dateValueGetter,
    valueFormatter: (value) => dateValueFormatter(value),
}

/*let not_before = {
    field: 'not_before',
    type: 'boolean',
    headerName: 'Not Before',
    width: 120,
}*/


export default function RushIconTooltip(params) {
    const value = params?.value;
    if (!value) return <Box/>;

    return (
        <Tooltip title="Rush order" arrow>
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                }}
            >
                <IconButton disableRipple sx={{cursor: "default"}}><LocalFireDepartmentIcon color="warning"
                                                                                            fontSize="small"/></IconButton>
            </Box>
        </Tooltip>
    );
}

let rush = {
    field: 'rush', type: 'boolean', headerName: 'Rush', width: 80, renderCell: RushIconTooltip
}

export function expressIconCell(title = "Express shipment") {
    return function ExpressIconCell(params) {
        const value = params?.value;
        if (!value) return <Box/>;

        const auto = !!params?.row?.express_auto;

        return (
            <Tooltip arrow title={auto ? "Express - booked with the carrier automatically"
                                       : "Express - arranged and documented by hand"}>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "100%",
                    }}
                >
                    <img src={auto ? ExpressIconSrc : ExpressManualIconSrc}
                         alt={auto ? "Express" : "Express, arranged manually"}
                         style={{height: auto ? 20 : 26, width: 'auto'}}/>
                </Box>
            </Tooltip>
        );
    };
}

export const ExpressIconTooltip = expressIconCell();

let express = {
    field: 'express', type: 'boolean', headerName: 'Is Express', width: 90, renderCell: ExpressIconTooltip
}


// singleSelect rather than free text: the bands are a fixed vocabulary, and the cell shows
// the title while the value is the code - so typing "Next Day" would never match.
let express_service = {
    field: 'express_service', type: 'singleSelect', headerName: 'Service Level', width: 150,
    valueOptions: () => Object.entries(serviceLevelTitles).map(([value, label]) => ({value, label})),
    renderCell: (params) => <ServiceLevelCell value={params.value}/>
}

let epoc_delivered = {
    field: 'epoc_delivered',
    numeric: false, headerName: 'Delivery', width: 135,
    valueGetter: dateValueGetter,
    valueFormatter: (value) => timestampValueFormatter(value),
    type: 'date',
}

let bolnum = {
    field: 'bolnum', type: 'string', disablePadding: false, headerName: 'BOL', width: 120,
}

export {
    express_service,
    salesorderid,
    shipmentid,
    destination,
    courier,
    bundles,
    weightkg,
    epoc_created,
    epoc_requested,
    epoc_pickupest,
    epoc_pickedup,
    epoc_deliveryest,
    epoc_delivered,
    bolnum,
    epoc_packed,
    po,
    customer,
    priority,
    finishes,
    pack_by,
    prod_release,
    rush,
    express,
    shipping_notes,
    plan_ship,
    pack_by_with_warnings,
    plan_ship_with_warnings,
    bundle_status,
    bundle_warning
};